import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { Star, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function Satisfaction() {
  const { ticket_id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/satisfaction/${ticket_id}?token=${encodeURIComponent(token)}`);
        setTicket(data.ticket);
        if (data.ticket?.satisfaction) setDone(true);
      } catch (e) {
        setErr(e.response?.data?.detail || "Lien invalide ou expiré.");
      } finally {
        setLoading(false);
      }
    })();
  }, [ticket_id, token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return setErr("Merci de choisir une note de 1 à 5.");
    setBusy(true); setErr("");
    try {
      await api.post(`/satisfaction/${ticket_id}`, { token, rating, comment: comment.trim() });
      setDone(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a10]">
        <Loader2 className="h-6 w-6 text-cyan-400 animate-spin" />
      </div>
    );
  }
  if (err && !ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a10] px-4">
        <div className="max-w-md text-center text-zinc-300">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <h1 className="text-lg font-semibold mb-2">Lien invalide</h1>
          <p className="text-sm text-zinc-400">{err}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#0a0a10] py-12 px-4 flex items-start justify-center">
      <div className="w-full max-w-lg" data-testid="satisfaction-page">
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-7">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Demande {ticket?.id}</div>
          <h1 className="text-2xl font-semibold text-white mb-2">Votre avis sur notre support</h1>
          <p className="text-sm text-zinc-400 leading-relaxed mb-6">
            Bonjour {ticket?.customer_name || ""}, votre demande
            « <span className="text-cyan-300">{ticket?.subject}</span> » a été résolue.
            Merci de prendre 30 secondes pour évaluer notre intervention — votre retour nous aide à progresser.
          </p>

          {done ? (
            <div className="bg-emerald-400/10 border border-emerald-400/30 rounded-xl p-5 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
              <h2 className="text-lg font-semibold text-emerald-200 mb-1">Merci pour votre retour !</h2>
              <p className="text-xs text-zinc-400">Votre avis a bien été enregistré.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Votre note</div>
                <div className="flex gap-2 justify-center" data-testid="satisfaction-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      type="button"
                      key={n}
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`star-${n}`}
                    >
                      <Star
                        className={`h-10 w-10 ${
                          (hover || rating) >= n
                            ? "fill-amber-400 text-amber-400"
                            : "text-zinc-600"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <div className="text-center text-xs text-zinc-500 mt-2 min-h-[16px]">
                  {(hover || rating) === 1 && "Très insatisfait"}
                  {(hover || rating) === 2 && "Insatisfait"}
                  {(hover || rating) === 3 && "Correct"}
                  {(hover || rating) === 4 && "Satisfait"}
                  {(hover || rating) === 5 && "Très satisfait"}
                </div>
              </div>

              <div>
                <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-2">
                  Commentaire (optionnel)
                </label>
                <textarea
                  rows={4}
                  maxLength={2000}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ce qui a été apprécié, ce qui peut être amélioré…"
                  className="w-full px-3 py-2 rounded-lg text-sm bg-white/[0.03] border border-white/10 focus:border-cyan-400/50 outline-none resize-none"
                  data-testid="satisfaction-comment"
                />
              </div>

              {err && <div className="text-xs text-red-300">{err}</div>}

              <button
                type="submit"
                disabled={busy || !rating}
                className="btn-primary w-full py-3 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="satisfaction-submit"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Envoyer mon avis
              </button>
            </form>
          )}
        </div>
        <div className="text-center text-[10px] text-zinc-600 mt-4">— Dr Trade Bots —</div>
      </div>
    </div>
  );
}
